<template>
    <div id="new-youtube-channel-form">
        <a-form>
            <div class="form-wrapper">
                <div class="input-wrapper">
                    <label>{{ $t("youtubeChannel.channelName") }}</label>
                    <a-form-item
                        :validate-status="getErrorStatus('channelName')"
                        :help="getErrorMessage('channelName')"
                    >
                        <a-input
                            v-model="form.channelName"
                            :placeholder="$t('youtubeChannel.channelName')"
                            name="channelName"
                            ref="channelName"
                        />
                    </a-form-item>
                </div>

                <div class="connect-body">
                    <div class="requirements">
                        <div><i class="material-icons">info</i>{{ $t("youtubeChannel.ensureYouTubeAccount") }}</div>
                        <div><i class="material-icons">info</i>{{ $t("youtubeChannel.ensureYouTubeChannel") }}</div>
                    </div>

                    <strong> {{ $t("youtubeChannel.permissionDesc") }} </strong>

                    <div>
                        <a-checkbox :checked="termOfService" @change="acceptTermOfService('pp')"></a-checkbox>
                        <b
                            >{{ $t("search.integration.readAndAgree") }}
                            <a @click="acceptTermOfService('pp')">{{ $t("search.integration.privatePolicy") }}</a>
                            {{ $t("message.and") }}
                            <a @click="acceptTermOfService('tac')">{{
                                $t("search.integration.termsAndCondition")
                            }}</a></b
                        >
                    </div>
                </div>
            </div>
        </a-form>

        <div class="form-footer">
            <div class="actions">
                <SupsisButton ghost @click="handleCancel"> {{ $t("wp.cancel") }}</SupsisButton>
                <SupsisButton
                    primary
                    @click="handleSubmit"
                    :disabled="!termOfService || loading"
                    :loading="loading"
                >
                    {{ $t("youtubeChannel.connectToYouTube") }}
                </SupsisButton>
            </div>
        </div>
        <TermOfService
            v-if="termOfServiceModal"
            :type="termOfServiceModal"
            @agree="acceptedTermOfService"
            @cancel="termOfServiceModal = null"
        />
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import CONFIG from "../../../../config/config";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import TermOfService from "../../../../components/TermOfService.vue";
import { socketMessageStream } from "../../../../utils/socketStream";
import { filter } from "rxjs/operators";

export default {
    name: "CreateYouTubeChannel",
    components: {
        TermOfService,
        SupsisButton,
    },
    data() {
        return {
            form: {
                channelName: "YouTube Comments",
            },
            loading: false,
            submitClicked: false,
            termOfService: false,
            termOfServiceModal: false,
            oauthWindow: null,
            cancels: [],
            checkWindowInterval: null,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site?._id,
            userId: (state) => state.auth.user?._id || state.auth.adminUser?._id,
        }),
    },
    methods: {
        ...mapActions({
            createChannel: "channel/createChannel",
            listChannels: "channel/listChannels",
        }),
        listenSocket() {
            const sub = socketMessageStream.pipe(filter((x) => x.cmd === "youtube_success")).subscribe((x) => {
                this.$message.success(this.$t("youtubeChannel.connectedSuccess"));
                this.listChannels();
                this.loading = false;
                if (this.oauthWindow) {
                    this.oauthWindow.close();
                }
                if (this.checkWindowInterval) {
                    clearInterval(this.checkWindowInterval);
                }
                this.$emit("ok");
            });
            this.cancels.push(() => sub.unsubscribe());
        },
        acceptTermOfService(type) {
            this.termOfServiceModal = type || "pp";
        },
        acceptedTermOfService() {
            this.termOfServiceModal = null;
            this.termOfService = true;
        },
        getErrorStatus(fieldName) {
            return this.form[fieldName].length < 1 && this.submitClicked ? "error" : "";
        },
        getErrorMessage(fieldName) {
            return this.form[fieldName].length < 1 && this.submitClicked ? this.$t("youtubeChannel.requiredChannel") : "";
        },
        handleCancel() {
            this.$emit("cancel");
        },
        async handleSubmit() {
            this.submitClicked = true;
            if (this.form.channelName.length < 1) {
                this.$refs.channelName.focus();
                return;
            }

            this.loading = true;

            try {
                const channelData = {
                    type: "youtube-comments",
                    name: this.form.channelName,
                    payload: {},
                };

                const channel = await this.createChannel(channelData);
                this.startOauth(channel._id);
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.failedToCreateChannel"));
                this.loading = false;
            }
        },
        startOauth(channelId) {
            const { CLIENT_ID, SCOPE, REDIRECT_URI, OAUTH2_ENDPOINT } = CONFIG.YOUTUBE;
            const state = `${this.siteId}/${channelId}`;

            const params = new URLSearchParams({
                client_id: CLIENT_ID,
                redirect_uri: REDIRECT_URI,
                response_type: "code",
                scope: SCOPE,
                access_type: "offline",
                prompt: "consent",
                state: state,
            });

            const url = `${OAUTH2_ENDPOINT}?${params.toString()}`;

            const width = 600;
            const height = 700;
            const left = (window.screen.width - width) / 2;
            const top = (window.screen.height - height) / 2;
            const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`;

            this.oauthWindow = window.open(url, "YouTubeOAuth", features);

            this.checkWindowInterval = setInterval(() => {
                if (this.oauthWindow && this.oauthWindow.closed) {
                    clearInterval(this.checkWindowInterval);
                    this.checkWindowInterval = null;
                }
            }, 1000);

            setTimeout(() => {
                if (this.checkWindowInterval) {
                    clearInterval(this.checkWindowInterval);
                }
                if (this.loading) {
                    this.loading = false;
                    this.$message.warning(this.$t("youtubeChannel.oauthTimeout"));
                }
            }, 5 * 60 * 1000);
        },
    },
    mounted() {
        this.listenSocket();
    },
    beforeDestroy() {
        this.cancels.forEach((c) => c());
        if (this.oauthWindow && !this.oauthWindow.closed) {
            this.oauthWindow.close();
        }
        if (this.checkWindowInterval) {
            clearInterval(this.checkWindowInterval);
        }
    },
};
</script>

<style lang="scss" scoped>
#new-youtube-channel-form {
    min-height: 250px;
    flex-direction: column;

    .form-wrapper {
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .input-wrapper {
            display: flex;
            flex-direction: column;
            gap: 5px;
            height: 75px;
        }
    }

    .form-footer {
        display: flex;
        justify-content: flex-end;
        padding: 10px;
        margin-top: 20px;
        background: var(--color-bg-default);
        border-top: 1px solid var(--color-border-default);

        .actions {
            display: flex;
            gap: 10px;
        }
    }

    .connect-body {
        display: flex;
        flex-direction: column;
        gap: 5px;
        justify-content: center;

        .requirements {
            display: flex;
            flex-direction: column;
            gap: 5px;

            div {
                display: flex;
                gap: 5px;
                align-items: center;
                justify-content: flex-start;

                .material-icons {
                    color: var(--color-text-info-default);
                    font-size: 13px;
                }
            }
        }
    }
}
</style>
